import { ContactRemovalTriggerEnum } from '@ben/api/gen/codegen.schemas';
import Select from '@ben/orion/deprecated-select';
import Input from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { capitalize } from 'lodash';
import { Fragment, useEffect, type ChangeEvent } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../../shared/constants';
import ModularStepComponentWrapper from '../../steps/modular-step-component-wrapper';
import { acceptedContactTypesOptions } from '../_common/acceptedContactTypesOptions';
import { removalTriggerOptions } from '../_common/removalTriggerOptions';

interface IModularContactsInputProps {
  componentControllerName: string;
}

const allContactType = 'ALL';

const removalTriggerContactKeys = acceptedContactTypesOptions.map((option) =>
  option.key.toLowerCase()
);

const ModularContactsInput = ({
  componentControllerName,
}: IModularContactsInputProps) => {
  const { control, setValue, watch, trigger } = useFormContext();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const acceptedContactTypesValue =
    useWatch({
      name: `${componentControllerName}.acceptedContactTypes`,
      exact: true,
    }) ?? [];

  useEffect(() => {
    for (const [index, contactType] of [
      allContactType,
      ...acceptedContactTypesValue,
    ].entries()) {
      if (contactType === allContactType) {
        continue;
      }
      setValue(
        `${componentControllerName}.subForms.${index}.contactType`,
        contactType
      );
    }
  }, [acceptedContactTypesValue, setValue, componentControllerName]);

  useEffect(() => {
    if (acceptedContactTypesValue.includes('SPOUSE')) {
      const spouseMinAge = watch(`${componentControllerName}.minAgeSpouse`);
      if (spouseMinAge) return;
      setValue(`${componentControllerName}.minAgeSpouse`, '18');
    }
  }, [acceptedContactTypesValue, componentControllerName, setValue, watch]);

  // Keep removalTriggers a complete object derived from the accepted types: an
  // accepted type keeps its value (defaulting when unset), a non-accepted type
  // resets to END_OF_POLICY_YEAR so a removed type never leaves a stale rule.
  useEffect(() => {
    const acceptedKeys = new Set(
      acceptedContactTypesValue.map((contactType: string) =>
        contactType.toLowerCase()
      )
    );
    for (const contactKey of removalTriggerContactKeys) {
      const removalTriggerName = `${componentControllerName}.removalTriggers.${contactKey}`;
      if (!acceptedKeys.has(contactKey) || !watch(removalTriggerName)) {
        setValue(
          removalTriggerName,
          ContactRemovalTriggerEnum.END_OF_POLICY_YEAR
        );
      }
    }
  }, [acceptedContactTypesValue, componentControllerName, setValue, watch]);

  return (
    <>
      <ComponentsWrapper>
        <Controller
          control={control}
          name={`${componentControllerName}.acceptedContactTypes`}
          render={({ field, fieldState }) => (
            <Select
              {...field}
              defaultValue={field.value ?? []}
              name="Accepted contact types"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              options={acceptedContactTypesOptions}
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
          }}
        />
        <Controller
          control={control}
          name={`${componentControllerName}.max`}
          render={({ field }) => (
            <Input
              {...field}
              type="number"
              name="Max number of contacts allowed (optional)"
              hintText="Determines how many dependents of all types an employee can add to this benefit."
            />
          )}
        />
      </ComponentsWrapper>
      <Divider />
      <Typography variant={TypographyVariant.bodyLg2}>Restrictions</Typography>
      <Typography variant={TypographyVariant.bodySm1}>
        Note: Maximum age is exclusive. For example, setting 25 will exclude age
        25 and only accept younger contacts.
      </Typography>
      {acceptedContactTypesValue.map((contactType: string) => {
        const isSpouse = contactType === 'SPOUSE';

        const capitalizedContactType = capitalize(contactType);

        const minAgeName = `${componentControllerName}.minAge${capitalizedContactType}`;
        const maxAgeName = `${componentControllerName}.maxAge${capitalizedContactType}`;

        const validateMinAge = (value: string) => {
          const maxAge = watch(maxAgeName);
          if (!maxAge) {
            return true;
          }
          if (isSpouse && Number(value) < 16) {
            return 'Min age for spouse cannot be less than 16';
          }
          if (Number(value) >= Number(maxAge)) {
            return 'Min age must be less than max age';
          }
          return true;
        };

        const validateMaxAge = (value: string) => {
          if (!value) {
            return true;
          }
          const minAge = watch(minAgeName);

          if (Number(value) <= Number(minAge)) {
            return 'Max age must be greater than min age';
          }

          if (Number(value) > 120) {
            return 'Max age cannot be greater than 120';
          }
          return true;
        };

        return (
          <RestrictionRow key={contactType}>
            <Input
              value={
                acceptedContactTypesOptions.find(
                  (contactTypeObject) => contactTypeObject.key === contactType
                )?.label
              }
              name="Contact type"
              width="168px"
              disabled
            />
            <Controller
              control={control}
              name={minAgeName}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="number"
                  name={`Min age${isSpouse ? '' : ' (optional)'}`}
                  width="100px"
                  hasError={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    field.onChange(event);
                    void trigger(maxAgeName);
                  }}
                />
              )}
              rules={{ validate: validateMinAge }}
            />
            <Controller
              control={control}
              name={maxAgeName}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="number"
                  name="Max age (optional)"
                  width="100px"
                  hasError={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    field.onChange(event);
                    void trigger(minAgeName);
                  }}
                />
              )}
              rules={{ validate: validateMaxAge }}
            />
            <Controller
              control={control}
              name={`${componentControllerName}.max${capitalizedContactType}`}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  name="Max # contacts"
                  width="100px"
                  hintText="Determines how many dependents of this contact type an employee can add to this benefit."
                />
              )}
            />
            <Controller
              control={control}
              name={`${componentControllerName}.removalTriggers.${contactType.toLowerCase()}`}
              render={({ field }) => (
                <Select
                  {...field}
                  value={
                    field.value ?? ContactRemovalTriggerEnum.END_OF_POLICY_YEAR
                  }
                  name="Removal trigger"
                  width="175px"
                  options={removalTriggerOptions}
                />
              )}
            />
          </RestrictionRow>
        );
      })}
      <Divider />
      {[allContactType, ...acceptedContactTypesValue].map(
        (contactType, index) => {
          return (
            <Fragment key={contactType}>
              <ModularStepComponentWrapper
                key={contactType}
                componentControllerName={`${componentControllerName}.subForms.${index}.components`}
                isSubForm
                subFormType={`${capitalize(contactType)} contacts type`}
              />
              <Divider />
            </Fragment>
          );
        }
      )}
    </>
  );
};

export default ModularContactsInput;

const ComponentsWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;

// Restriction rows lay out fixed-width columns with a 24px gap, per the design.
// Labels stay on one line (overflowing into the gap, as in the design) so a
// long heading can't wrap and push its input below the others.
const RestrictionRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(3)};

    label {
      white-space: nowrap;
    }
  `}
`;

const Divider = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 1px;
    background: ${theme.palette.neutral[60]};
  `}
`;
